import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalyticsFilters } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import { advancedPostToQuery } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/advanced-filters/advanced-filters.types';
import { SlMatchTypeFilter } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/match-type/match-type.component';
import { recoverUnlessStrict } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/recover-unless-strict';
import { smiUrl } from 'components';
import { map, Observable } from 'rxjs';

export interface TopInfluencerByVoiceShare {
	name: string;
	handle: string;
	avatarUrl: string | null;
	platform: string;
	platformIconUrl: string | null;
	mentions: number;
	mentionsNegative: number;
	mentionsNeutral: number;
	mentionsPositive: number;
	voiceShare: number;
}

interface TopInfluencerApi {
	name: string;
	handle: string;
	avatar_url: string;
	platform: string;
	mentions: number;
	mentions_negative: number;
	mentions_neutral: number;
	mentions_positive: number;
	voice_share: number;
}

@Injectable({
	providedIn: 'root'
})
export class GetSocialListeningMyBrandAnalyticsTopInfluencersByVoiceShareService {
	constructor(private http: HttpClient) {}

	private applyFilters(
		b: any,
		filters?: AnalyticsFilters,
		matchType?: SlMatchTypeFilter
	): void {
		if (filters?.platforms?.length)
			b.query('platforms', filters.platforms.join(','));
		if (filters?.sentiments?.length)
			b.query('sentiments', filters.sentiments.join(','));
		if (filters?.emotions?.length)
			b.query('emotions', filters.emotions.join(','));
		if (filters?.languages?.length)
			b.query('languages', filters.languages.join(','));
		if (filters?.engagementRate) {
			b.query('engagement_rate_min', String(filters.engagementRate[0]));
			b.query('engagement_rate_max', String(filters.engagementRate[1]));
		}
		if (matchType) b.query('match_type', matchType);
		for (const [advKey, advVal] of Object.entries(
			advancedPostToQuery(filters?.advanced)
		)) {
			b.query(advKey, advVal);
		}
	}

	fetch(
		brandId: string,
		cutoff?: Date | null,
		filters?: AnalyticsFilters,
		to?: Date | null,
		matchType?: SlMatchTypeFilter,
		strict = false
	): Observable<TopInfluencerByVoiceShare[]> {
		const urlBuilder = smiUrl()
			.add('social-listening')
			.add('brand')
			.add(brandId)
			.add('analytics-top-influencers-by-voice-share')
			.query('from', cutoff?.toISOString())
			.query('to', to?.toISOString());
		this.applyFilters(urlBuilder, filters, matchType);
		const url = urlBuilder.done();

		return this.http.get<{ items: TopInfluencerApi[] }>(url).pipe(
			recoverUnlessStrict(strict, () => ({
				items: [] as TopInfluencerApi[]
			})),
			map(r => (r.items ?? []).map(i => this.mapApi(i)))
		);
	}

	private mapApi(i: TopInfluencerApi): TopInfluencerByVoiceShare {
		return {
			name: i.name,
			handle: i.handle,
			avatarUrl: i.avatar_url || null,
			platform: i.platform,
			platformIconUrl: null,
			mentions: i.mentions,
			mentionsNegative: i.mentions_negative,
			mentionsNeutral: i.mentions_neutral,
			mentionsPositive: i.mentions_positive,
			voiceShare: i.voice_share
		};
	}
}
